// default direction per sort field, matching the API examples
const sortOrderMap = {
  priority: 'desc',
  effortScore: 'desc',
  deadline: 'asc',
};

export const emptyTaskFilter = {
  sortBy: null, // null = manual (sortIndex)
  userIds: [],
  withoutUsers: false,
  search: '',
  // finished tasks are noise most of the time, so they start hidden
  hideDone: true,
  // phases and work packages without a task to show start hidden too
  hideEmpty: true,
};

/**
 * Whether a work package has anything to list under the filter. Decided on
 * its task counters, so it works without loading the tasks - with hideDone
 * a package of only finished tasks counts as empty as well.
 */
export function isWorkPackageVisible(workPackage, { hideDone, hideEmpty } = {}) {
  if (!hideEmpty) return true;

  const stats = workPackage.taskStats || {};
  const total = stats.totalTasks || 0;

  return hideDone ? total - (stats.doneTasks || 0) > 0 : total > 0;
}

// a phase is only worth a row when one of its work packages is
export function visiblePhases(phases = [], filter = {}) {
  if (!filter.hideEmpty) return phases;

  return phases.filter((phase) =>
    (phase.workPackages || []).some((workPackage) =>
      isWorkPackageVisible(workPackage, filter),
    ),
  );
}

/**
 * Query string for work-packages/{id}/tasks, e.g.
 * ?sortBy=priority&sortOrder=desc&userIds[]=15&userIds[]=17
 * withoutUsers is sent as a bare flag, the way the API expects it.
 */
export const TASKS_PER_PAGE = 10;

export function buildTaskFilterQuery({
  sortBy,
  userIds,
  withoutUsers,
  search,
  hideDone,
} = {}) {
  const parts = [`perPage=${TASKS_PER_PAGE}`];

  if (search?.trim()) parts.push(`search=${encodeURIComponent(search.trim())}`);

  // status 0 = open, so hiding the finished tasks is a status filter
  if (hideDone) parts.push('status[eq]=0');

  if (sortBy) {
    parts.push(`sortBy=${sortBy}`);
    if (sortOrderMap[sortBy]) parts.push(`sortOrder=${sortOrderMap[sortBy]}`);
  }

  // the filter is either by users or by the missing assignee
  if (withoutUsers) {
    parts.push('withoutUsers=true');
  } else {
    (userIds || []).forEach((id) => parts.push(`userIds[]=${id}`));
  }

  return `?${parts.join('&')}`;
}

/**
 * Safeguard over the order the API returns: earliest deadline first, highest
 * priority/effort first, and entries without a value always last.
 * Manual sorting is left to the API, which returns the tasks by sortIndex.
 */
export function sortTasks(tasks, sortBy) {
  if (!sortBy) return tasks;

  const direction = sortOrderMap[sortBy] === 'asc' ? 1 : -1;

  return [...tasks].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    const aEmpty = aValue === null || aValue === undefined;
    const bEmpty = bValue === null || bValue === undefined;
    if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;

    if (sortBy === 'deadline')
      return (new Date(aValue) - new Date(bValue)) * direction;

    return (aValue - bValue) * direction;
  });
}
